import {type RefObject, useEffect, useId} from 'react';

import {useCompareViewportStore} from './compareViewportStore';

export type CompareViewportView = 'workspace' | 'fullscreen';

type Props = {
  panelId: string;
  view: CompareViewportView;
  isSynced: boolean;
  controlRef: RefObject<HTMLDivElement | null>;
  menuRef: RefObject<HTMLDivElement | null>;
  activeOwnerRef: RefObject<string | null>;
};

/** A panel owns shortcuts until interaction moves outside its view and zoom menu. */
export function useCompareZoomActivation({
  panelId,
  view,
  isSynced,
  controlRef,
  menuRef,
  activeOwnerRef,
}: Props) {
  const ownerId = useId();
  const isActive = useCompareViewportStore(
    state => state.activePanelOwnerId === ownerId
  );
  const setActivePanel = useCompareViewportStore(state => state.setActivePanel);
  const releaseActivePanel = useCompareViewportStore(
    state => state.releaseActivePanel
  );

  useEffect(() => {
    activeOwnerRef.current = null;
    if (!isSynced) {
      return;
    }
    const panel = controlRef.current?.closest(
      view === 'fullscreen'
        ? '[data-test="full-screen-panel-container"]'
        : '[data-test="workspace-panel-container"]'
    );
    if (panel == null) {
      return;
    }
    const claimOrRelease = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      // The modal's own input scope takes priority without changing the
      // originating panel. Closing it may return focus to the document body.
      if (
        target instanceof Element &&
        target.closest('[data-test="media-modal-root"]') != null
      ) {
        return;
      }
      // The zoom menu renders in a portal outside the panel; interacting with
      // it keeps the panel's ownership.
      if (panel.contains(target) || menuRef.current?.contains(target)) {
        activeOwnerRef.current = ownerId;
        setActivePanel(panelId, ownerId);
      } else {
        activeOwnerRef.current = null;
        releaseActivePanel(ownerId);
      }
    };
    // Fullscreen is the only panel on screen, so it owns shortcuts on mount.
    if (view === 'fullscreen') {
      activeOwnerRef.current = ownerId;
      setActivePanel(panelId, ownerId);
    }
    document.addEventListener('pointerdown', claimOrRelease, true);
    document.addEventListener('focusin', claimOrRelease, true);
    return () => {
      activeOwnerRef.current = null;
      document.removeEventListener('pointerdown', claimOrRelease, true);
      document.removeEventListener('focusin', claimOrRelease, true);
      releaseActivePanel(ownerId);
    };
  }, [
    controlRef,
    isSynced,
    menuRef,
    activeOwnerRef,
    ownerId,
    panelId,
    releaseActivePanel,
    setActivePanel,
    view,
  ]);

  return isSynced && isActive;
}
